/**
 * Browser-side bot handling for posthog-js on top of the SDK's own UA blocklist
 * (DEFAULT_BLOCKED_UA_STRS in @posthog/core, which already drops Googlebot, GoogleOther,
 * meta-externalagent, GPTBot, OAI-SearchBot and friends).
 *
 * Two layers with different risk:
 * - `isAutomationUserAgent` drops events. It only matches explicit automation and crawler
 *   tokens, never a bare "bot" substring, so phone brands such as CUBOT keep their analytics.
 * - `suspectedBotSignals` never drops anything. It returns window-geometry fingerprints of the
 *   headless fleets seen from 2026-10-01 (800x600 windows, zh-CN anti-detect screens). The
 *   caller registers them as super properties so reports can exclude them with
 *   `properties.suspected_bot` while a misjudged customer still shows up in the data.
 */

const AUTOMATION_UA =
	/HeadlessChrome|puppeteer|playwright|Selenium|crawler|spider|\bbot\b|bot[/;_)-]|bot\.htm/i;

export function isAutomationUserAgent(userAgent: string): boolean {
	return AUTOMATION_UA.test(userAgent);
}

export interface BrowserGeometry {
	viewportWidth: number;
	viewportHeight: number;
	screenWidth: number;
	screenHeight: number;
	language: string;
	maxTouchPoints: number;
}

export function readBrowserGeometry(): BrowserGeometry | null {
	try {
		if (typeof window === "undefined" || typeof navigator === "undefined") return null;
		return {
			viewportWidth: window.innerWidth,
			viewportHeight: window.innerHeight,
			screenWidth: window.screen.width,
			screenHeight: window.screen.height,
			language: navigator.language,
			maxTouchPoints: navigator.maxTouchPoints ?? 0,
		};
	} catch {
		return null;
	}
}

/** Fingerprints that matched 0 sessions with a cart, checkout or order event in PostHog (29.09.-06.10.2026). */
export function suspectedBotSignals(geometry: BrowserGeometry | null): string[] {
	if (!geometry || geometry.maxTouchPoints > 0) return [];
	const { viewportWidth: vw, viewportHeight: vh, screenWidth: sw, screenHeight: sh } = geometry;
	const signals: string[] = [];
	if (vw === 800 && vh === 600) signals.push("viewport_800x600");
	if (sw === 800 && sh === 600) signals.push("screen_800x600");
	if (
		geometry.language === "zh-CN" &&
		vw === sw &&
		sh - vh >= 100 &&
		sh - vh <= 140 &&
		sw >= 1200 &&
		sw <= 1399 &&
		sh >= 1000 &&
		sh <= 1330 &&
		sw > sh
	) {
		signals.push("zh_random_screen");
	}
	return signals;
}
